import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiTag,
  FiUsers,
} from "react-icons/fi";
import safariThumb from "../assets/safari-banner.jpg";

const fallbackSafari = {
  id: null,
  title: "Uganda Gorillas & Tanzania Safari",
  description:
    "Explore primates, gorillas, game drives, and breathtaking East African scenery on a carefully planned safari journey.",
  image: safariThumb,
  startLocation: "Entebbe",
  destination: "Uganda & Tanzania",
  typeOfSafari: ["Gorilla Trekking", "Wildlife Safari"],
  itinerary: Array.from({ length: 10 }),
};

const getDuration = (safari) => {
  if (safari.durationLabel) return safari.durationLabel;

  const match = safari.title?.match(/\d+\s*(Days|Nights)/i);
  if (match) return match[0];

  if (Array.isArray(safari.itinerary) && safari.itinerary.length > 0) {
    return `${safari.itinerary.length} Days`;
  }

  return "Custom Trip";
};

const getDestination = (safari) => {
  if (safari.destination) return safari.destination;
  if (Array.isArray(safari.countries) && safari.countries.length > 0) {
    return safari.countries.join(" & ");
  }
  return safari.startLocation || "East Africa";
};

const getPrice = (safari) => {
  const amount = safari.price?.discounted || safari.price?.original;
  if (!amount) return "Quote";

  return `${safari.price?.currency || "USD"} ${amount.toLocaleString()}`;
};

const getGuestLabel = (safari) => {
  if (!safari.minimumGuests) return null;
  return `Min ${safari.minimumGuests} Guests`;
};

const SafariCard = ({ safari = fallbackSafari, index = 0 }) => {
  const cardData = { ...fallbackSafari, ...safari };
  const categories = cardData.typeOfSafari?.slice(0, 2) || [];
  const linkTarget = cardData.id ? `/safari/${cardData.id}` : "/tours";

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: "easeOut" }}
      whileHover={{ y: -8 }}
      className="group h-full overflow-hidden rounded-sm border border-[#eadfbe] bg-white shadow-xl shadow-black/10"
    >
      <Link to={linkTarget} className="flex h-full flex-col">
        <div className="relative h-64 overflow-hidden">
          <img
            src={cardData.image || safariThumb}
            alt={cardData.title}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-sm bg-[#F5B731] px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-[#082f1f]">
              <FiCalendar />
              {getDuration(cardData)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-sm bg-[#082f1f]/90 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-white">
              <FiTag />
              {getPrice(cardData)}
            </span>
          </div>

          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#F5B731]">
              <FiMapPin />
              {getDestination(cardData)}
            </div>
            <h3 className="text-xl font-extrabold leading-snug">
              {cardData.title}
            </h3>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <p className="line-clamp-3 text-sm leading-7 text-[#5d7168]">
            {cardData.description}
          </p>

          {categories.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.map((category) => (
                <span
                  key={category}
                  className="rounded-sm bg-[#f6f1e7] px-3 py-1 text-xs font-bold text-[#315245]"
                >
                  {category}
                </span>
              ))}
            </div>
          )}

          <div className="mt-6 space-y-2 border-t border-[#eadfbe] pt-5 text-sm text-[#315245]">
            <div className="flex items-center gap-2">
              <FiMapPin className="shrink-0 text-[#C88804]" />
              <span className="line-clamp-1">
                Starts: {cardData.startLocation || "Flexible"}
              </span>
            </div>
            {getGuestLabel(cardData) && (
              <div className="flex items-center gap-2">
                <FiUsers className="shrink-0 text-[#C88804]" />
                <span>{getGuestLabel(cardData)}</span>
              </div>
            )}
          </div>

          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-[#082f1f] transition group-hover:text-[#C88804]">
            View Details
            <FiArrowRight className="transition group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default SafariCard;
